import { Button, Flex } from '@chakra-ui/react'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'

import RPC from '@/client/rpc'
import { getRequestVolunteerActivityChangesFormInputs } from '@/client/services/formInputs/volunteerActivity'

import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import Tippy from '@/components/UI/Tippy'

export default function RejectButton({
  id,
  requiresChangesBeforeReview,
  onReject,
}: {
  id: number
  requiresChangesBeforeReview: boolean
  onReject?: () => void
}): JSX.Element {
  const router = useRouter()
  const toast = useToaster()
  const [showRejectModal, setShowRejectModal] = useState(false)

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (requiresChangesBeforeReview) {
        return
      }
      toast({
        status: 'info',
        title: 'Requesting changes to volunteer activity...',
      })
      await RPC.admin_requestVolunteerActivityChanges({ id, reason: `${values.reason}` })
      await router.replace(router.asPath, undefined, { scroll: false })
      if (onReject != null) {
        onReject()
      }
      toast({
        status: 'success',
        title: 'Successfully requested changes.',
      })
      setShowRejectModal(false)
    },
    [id, onReject, router, toast, requiresChangesBeforeReview]
  )

  return (
    <>
      <Tippy disabled={!requiresChangesBeforeReview} content="Changes have already been requested.">
        <Button
          onClick={() => setShowRejectModal(true)}
          rightIcon={<FontAwesomeIcon icon={faTimes} />}
          variant="outline"
          color="error.fg"
          isDisabled={requiresChangesBeforeReview}
        >
          Reject
        </Button>
      </Tippy>
      <ModalV3
        size="xl"
        title="Request Volunteer Activity Changes"
        open={showRejectModal}
        body={() => (
          <Form
            name="Request Volunteer Activity Changes"
            blocks={getRequestVolunteerActivityChangesFormInputs()}
            onSubmit={onSubmit}
            FooterComponent={({ submitting }) => (
              <Flex mt={4} justify="flex-end">
                <Button isLoading={submitting} variant="outline" color="error.fg" type="submit">
                  Request Changes
                </Button>
              </Flex>
            )}
          />
        )}
        onClose={() => setShowRejectModal(false)}
      />
    </>
  )
}
